/**
 * The path prefix this copy of the app is served under ("/eu" for the European workspace, "" otherwise).
 * The server tells the page in window.__VP_BASE__ (server/utils/base-path.ts). Everything that builds an address
 * by hand goes through withBase; wouter's Router, fetch and WebSocket are set up once in installBasePath.
 */

import { languageForRegionPath } from '@shared/regions';

declare global {
  interface Window {
    __VP_BASE__?: string;
  }
}

export const BASE_PATH: string = typeof window !== 'undefined' && typeof window.__VP_BASE__ === 'string' ? window.__VP_BASE__ : '';

/** The language a country prefix opens in until the visitor picks another (the table is shared/regions.ts). */
export function regionLanguage(base: string = BASE_PATH): string | undefined {
  return languageForRegionPath(base);
}

/**
 * Where the visitor's language choice is remembered. One key per prefix, so opening /germany in German does not turn
 * the bare address German, and a choice made under one prefix stays under that prefix.
 */
export const LANGUAGE_STORAGE_KEY: string = BASE_PATH ? `i18nextLng_${BASE_PATH.slice(1)}` : 'i18nextLng';

/** "/app/x" becomes "/eu/app/x" under a prefix; anything that is not a same-site absolute path is returned as it is. */
export function withBase(path: string, base: string = BASE_PATH): string {
  if (!base || !path.startsWith('/') || path.startsWith('//')) return path;
  if (path === base || path.startsWith(`${base}/`) || path.startsWith(`${base}?`)) return path;
  return `${base}${path}`;
}

/** The current page's path without the prefix, for code that looks at which page it is on. */
export function currentPath(): string {
  const path = window.location.pathname;
  if (!BASE_PATH) return path;
  if (path === BASE_PATH) return '/';
  return path.startsWith(`${BASE_PATH}/`) ? path.slice(BASE_PATH.length) : path;
}

let installed = false;

/** Points same-site fetch and WebSocket calls at the prefix. Does nothing without one. */
export function installBasePath(): void {
  if (installed || !BASE_PATH || typeof window === 'undefined') return;
  installed = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string') return nativeFetch(withBase(input), init);
    if (input instanceof URL) {
      if (input.origin === window.location.origin) return nativeFetch(withBase(`${input.pathname}${input.search}`), init);
      return nativeFetch(input, init);
    }
    const url = new URL(input.url, window.location.href);
    if (url.origin === window.location.origin) return nativeFetch(new Request(withBase(`${url.pathname}${url.search}`), input), init);
    return nativeFetch(input, init);
  }) as typeof window.fetch;

  const NativeWebSocket = window.WebSocket;
  const PrefixedWebSocket = function (this: unknown, url: string | URL, protocols?: string | string[]) {
    const target = new URL(String(url), window.location.href);
    if (target.host === window.location.host) target.pathname = withBase(target.pathname);
    return new NativeWebSocket(target.toString(), protocols);
  } as unknown as typeof WebSocket;
  PrefixedWebSocket.prototype = NativeWebSocket.prototype;
  Object.assign(PrefixedWebSocket, { CONNECTING: 0, OPEN: 1, CLOSING: 2, CLOSED: 3 });
  window.WebSocket = PrefixedWebSocket;

  // Plain <a href="/..."> links: give them the prefix just before they are used (pressed, tabbed to, right-clicked)
  const fixAnchor = (event: Event) => {
    const anchor = (event.target as Element | null)?.closest?.('a[href]');
    const href = anchor?.getAttribute('href');
    if (anchor && href) {
      const fixed = withBase(href);
      if (fixed !== href) anchor.setAttribute('href', fixed);
    }
  };
  for (const type of ['pointerdown', 'focusin', 'contextmenu']) document.addEventListener(type, fixAnchor, true);
}
